import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const markets = [
  { flag: "🇲🇾", country: "Malaysia", zh: "马来西亚", role: "HQ & Hub", color: "#FF1C35", priority: "CORE HQ", desc: "Headquarters · Multi-language production base · Regional AI & data command center.", focus: ["Malay / English content", "AI & platform systems", "SEA command center", "Multi-cultural localization"] },
  { flag: "🇮🇩", country: "Indonesia", zh: "印度尼西亚", role: "Growth Engine", color: "#c8a96e", priority: "PRIORITY", desc: "270M population · Largest SEA market · Mass-scale advertising & low CAC user acquisition.", focus: ["Mass ad distribution", "Low CAC acquisition", "Bahasa Indonesia content", "Highest MAU target"] },
  { flag: "🇹🇭", country: "Thailand", zh: "泰国", role: "Entertainment Core", color: "#7bc4e0", priority: "CORE", desc: "Mature entertainment consumers · High viral transmission · Premium brand-friendly content.", focus: ["Thai-language drama", "High virality content", "Entertainment community", "Premium brands"] },
  { flag: "🇻🇳", country: "Vietnam", zh: "越南", role: "High-Growth Market", color: "#82c996", priority: "CORE", desc: "Young demographic · High TikTok penetration · Low-cost growth validation market.", focus: ["TikTok short drama", "KOL partnerships", "Low CAC validation", "Youth audience"] },
  { flag: "🇹🇼", country: "Taiwan", zh: "台湾", role: "Premium ARPU", color: "#b07be0", priority: "CORE", desc: "Highest ARPU market · Mature traditional Chinese entertainment audience · Strong IP awareness.", focus: ["Premium membership", "IP collaboration", "Traditional Chinese", "Highest ARPU region"] },
];

const hqFunctions = [
  { icon: "🤖", label: "AI Systems", zh: "AI中台系统" },
  { icon: "📊", label: "Data Platform", zh: "数据系统" },
  { icon: "🌐", label: "Global IP", zh: "全球IP管理" },
  { icon: "💡", label: "Brand & Capital", zh: "品牌与资本叙事" },
  { icon: "⚙️", label: "Platform Tech", zh: "平台技术体系" },
  { icon: "📋", label: "KPI System", zh: "全球KPI体系" },
];

const agencyKpi = [
  { label: "Ad ROI Target", value: "200%+", icon: "📈" },
  { label: "D1 Retention", value: "35–50%", icon: "🔁" },
  { label: "7-Day Retention", value: "20–35%", icon: "📅" },
  { label: "CAC Range", value: "RM 15–40", icon: "💰" },
];

const AgencyModel = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const m = markets[active];

  return (
    <section id="agency" ref={ref} style={{ padding: "110px 40px", borderTop: "1px solid rgba(255,255,255,0.05)", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "linear-gradient(180deg,#080808 0%,rgba(255,28,53,0.03) 50%,#080808 100%)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={visible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 36, height: 2, background: "#FF1C35" }} />
            <span style={{ color: "#FF1C35", fontSize: 12, fontWeight: 700, letterSpacing: 3 }}>SEA AGENCY MODEL · 东南亚代理体系</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "start", marginBottom: 60 }} className="agency-header-grid">
            <div>
              <h2 style={{ fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, lineHeight: 1.08, margin: "0 0 18px" }}>
                Malaysia as the<br />
                <span style={{ color: "#FF1C35" }}>Command Center.</span><br />
                Southeast Asia as the<br />
                <span style={{ color: "#FF1C35" }}>Growth Network.</span>
              </h2>
              <p style={{ fontSize: 15, color: "rgba(255,255,255,0.5)", lineHeight: 1.8, margin: "0 0 12px" }}>
                MIRE operates a <strong style={{ color: "#fff" }}>lightweight dual-layer structure</strong>: Malaysia HQ controls AI systems, data, platform & global IP — while regional agency teams handle local content, user growth, advertising & community.
              </p>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.28)", lineHeight: 1.8 }}>
                总部统一掌控AI系统、数据系统、平台系统、全球IP及品牌；区域代理负责本地内容制作、用户增长、本地广告及社区运营。轻资产模式，快速多国复制。
              </p>
            </div>
            {/* HQ Functions */}
            <div>
              <div style={{ padding: "28px 24px", borderRadius: 18, background: "rgba(255,28,53,0.06)", border: "1px solid rgba(255,28,53,0.2)", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                  <span style={{ fontSize: 24 }}>🏢</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>Malaysia HQ — Control Tower</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>吉隆坡总部 · 全球中台</div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                  {hqFunctions.map(f => (
                    <div key={f.label} style={{ padding: "10px 8px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", textAlign: "center" }}>
                      <div style={{ fontSize: 18, marginBottom: 4 }}>{f.icon}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#fff" }}>{f.label}</div>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginTop: 2 }}>{f.zh}</div>
                    </div>
                  ))}
                </div>
              </div>
              {/* KPI Row */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
                {agencyKpi.map(k => (
                  <div key={k.label} style={{ padding: "12px 8px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", textAlign: "center" }}>
                    <div style={{ fontSize: 16 }}>{k.icon}</div>
                    <div style={{ fontSize: 15, fontWeight: 900, color: "#FF1C35", fontFamily: "JetBrains Mono,monospace", margin: "4px 0 2px" }}>{k.value}</div>
                    <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5 }}>{k.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Market Selector */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={visible ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.4, duration: 0.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <div style={{ width: 24, height: 2, background: "#FF1C35" }} />
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 700, letterSpacing: 3 }}>5 CORE MARKETS · 五大核心市场</span>
          </div>

          {/* Tab buttons */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
            {markets.map((mk, i) => (
              <button key={mk.country} onClick={() => setActive(i)}
                style={{
                  padding: "9px 18px", borderRadius: 999, border: `1px solid ${active === i ? mk.color : "rgba(255,255,255,0.1)"}`,
                  background: active === i ? `${mk.color}18` : "transparent",
                  color: active === i ? mk.color : "rgba(255,255,255,0.5)",
                  fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.2s",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                <span>{mk.flag}</span>{mk.country}
                {i === 0 && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 999, background: "#FF1C35", color: "#fff", letterSpacing: 1 }}>HQ</span>}
              </button>
            ))}
          </div>

          {/* Active market detail */}
          <motion.div key={active}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            style={{ padding: "32px 32px", borderRadius: 20, background: "rgba(255,255,255,0.03)", border: `1px solid ${m.color}30`, display: "grid", gridTemplateColumns: "auto 1fr", gap: 36, alignItems: "start" }}
            className="market-detail-grid"
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 64, lineHeight: 1 }}>{m.flag}</div>
              <div style={{ marginTop: 10, display: "inline-block", padding: "4px 12px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: `${m.color}20`, color: m.color, border: `1px solid ${m.color}35` }}>{m.priority}</div>
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
                <h3 style={{ fontSize: 26, fontWeight: 900, color: "#fff", margin: 0 }}>{m.country}</h3>
                <span style={{ fontSize: 13, color: "rgba(255,255,255,0.3)" }}>{m.zh}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: m.color }}>{m.role}</span>
              </div>
              <p style={{ fontSize: 15, color: "rgba(255,255,255,0.55)", lineHeight: 1.7, margin: "0 0 20px" }}>{m.desc}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {m.focus.map(f => (
                  <span key={f} style={{ padding: "6px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: `${m.color}12`, color: m.color, border: `1px solid ${m.color}25` }}>→ {f}</span>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Agency structure */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={visible ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.7, duration: 0.6 }}
          style={{ marginTop: 48 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <div style={{ width: 24, height: 2, background: "#FF1C35" }} />
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 700, letterSpacing: 3 }}>AGENCY TEAM STRUCTURE · 代理团队标准架构</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }} className="agency-team-grid">
            {[
              { icon: "🎬", en: "Content Team", zh: "本地内容制作" },
              { icon: "📱", en: "Growth Team", zh: "广告与用户增长" },
              { icon: "🤝", en: "KOL Team", zh: "社区传播" },
              { icon: "💼", en: "Business Team", zh: "品牌商务合作" },
              { icon: "📊", en: "Data Team", zh: "ROI优化分析" },
            ].map(t => (
              <div key={t.en} style={{ padding: "18px 14px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", textAlign: "center" }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>{t.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{t.en}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 3 }}>{t.zh}</div>
              </div>
            ))}
          </div>

          {/* 2026 Targets */}
          <div style={{ marginTop: 32, padding: "24px 32px", borderRadius: 16, background: "rgba(255,28,53,0.05)", border: "1px solid rgba(255,28,53,0.15)", display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 20 }}>
            {[["5–8", "Regional Agencies by 2026", "区域代理数量"], ["5 Countries", "Core Market Coverage", "核心覆盖国家"], ["RM 600–1000万", "Agency Revenue 2026", "代理分成收入"], ["100+", "Global Agents by 2030", "2030年全球代理目标"]].map(([v, l, zh]) => (
              <div key={l} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 26, fontWeight: 900, color: "#FF1C35", fontFamily: "JetBrains Mono,monospace" }}>{v}</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 4 }}>{l}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", marginTop: 2 }}>{zh}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
      <style>{`
        @media(max-width:900px){.agency-header-grid{grid-template-columns:1fr!important;gap:32px!important}.agency-team-grid{grid-template-columns:1fr 1fr!important}.market-detail-grid{grid-template-columns:1fr!important}}
      `}</style>
    </section>
  );
};
export default AgencyModel;
